// Admin Subscription Management Service
// Handles all API calls to the subscription management endpoints

export interface SubscriptionPlan {
    stripePriceId: string;
    stripeProductId: string;
    productName: string;
    description: string;
    amount: number;
    currency: string;
    interval: 'month' | 'year';
    active: boolean;
    features?: string[];
    sortOrder?: number;
}

export interface SubscriptionDetails {
    userId: string;
    firestoreData: {
        subscriptionStatus: string;
        stripePriceId?: string;
        stripeSubscriptionId?: string;
        stripeCustomerId?: string;
        billingExempt: boolean;
        cancelAtPeriodEnd: boolean;
        currentPeriodEnd?: string;
    };
    stripeData: {
        id: string;
        status: string;
        currentPeriodStart: string;
        currentPeriodEnd: string;
        cancelAtPeriodEnd: boolean;
        priceId: string;
        amount: number;
    } | null;
}

export interface SubscriptionHistoryEntry {
    logId: string;
    action: string;
    timestamp: string;
    adminEmail: string;
    adminUid: string;
    success: boolean;
    metadata: {
        oldPlanId?: string;
        newPlanId?: string;
        adminNotes?: string;
        reason?: string;
        immediate?: boolean;
    };
    ipAddress: string;
}

export interface SubscriptionHistory {
    userId: string;
    history: SubscriptionHistoryEntry[];
    count: number;
    hasMore: boolean;
}

export interface SubscriptionPreview {
    userId: string;
    currentPlan: {
        priceId: string;
        amount: number;
        currency: string;
        interval: string;
    };
    newPlan: {
        priceId: string;
        amount: number;
        currency: string;
        interval: string;
    };
    priceDifference: number;
    percentageChange: number;
    effectiveDate: string;
    nextBillingDate: string;
}

/**
 * Get the admin API base URL
 * In development: uses proxy (/api/admin)
 * In production: uses full Cloud Function URL
 */
function getAdminApiUrl(): string {
    const apiUrl = import.meta.env.VITE_ADMIN_API_URL;
    if (apiUrl) {
        return apiUrl;
    }
    // Fallback to proxy for development
    return '/api/admin';
}

const API_BASE_URL = `${getAdminApiUrl()}/admin/subscriptions`;
const ACCOUNTS_API_URL = `${getAdminApiUrl()}/admin/accounts`;
const AUDIT_API_URL = `${getAdminApiUrl()}/admin/audit`;

/**
 * Get admin JWT token for authenticated requests
 */
async function getAuthToken(): Promise<string> {
    const { getAdminJWT } = await import('./adminAuthService');
    return await getAdminJWT();
}

/**
 * List all available subscription plans
 */
export async function listSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/plans`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch subscription plans');
    }

    const data = await response.json();
    return data.plans;
}

/**
 * Search for a user by email address
 */
export async function searchUser(email: string): Promise<{ userId: string; email: string }> {
    const token = await getAuthToken();
    const response = await fetch(`${ACCOUNTS_API_URL}/search?q=${encodeURIComponent(email)}&type=email`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'User not found');
    }

    const data = await response.json();

    // Extract user from results array
    if (!data.results || data.results.length === 0) {
        throw new Error('User not found');
    }

    const user = data.results[0];
    return {
        userId: user.uid,
        email: user.email,
    };
}

/**
 * Get subscription details for a specific user
 */
export async function getUserSubscription(userId: string): Promise<SubscriptionDetails> {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/${userId}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch subscription details');
    }

    return await response.json();
}

/**
 * Update user subscription plan (no immediate charge)
 */
export async function updateUserSubscription(
    userId: string,
    newPlanId?: string,
    billingExempt?: boolean,
    adminNotes?: string
): Promise<any> {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/update`, {
        method: 'PUT',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            userId,
            newPlanId,
            billingExempt,
            adminNotes,
            effectiveDate: 'next_cycle',
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update subscription');
    }

    return await response.json();
}

/**
 * Preview subscription change before applying
 */
export async function previewSubscriptionChange(
    userId: string,
    newPlanId: string
): Promise<SubscriptionPreview> {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/preview`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            userId,
            newPlanId,
            effectiveDate: 'next_cycle',
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to preview subscription change');
    }

    return await response.json();
}

/**
 * Get subscription modification history
 */
export async function getSubscriptionHistory(
    userId: string,
    limit: number = 50
): Promise<SubscriptionHistory> {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/${userId}/history?limit=${limit}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch subscription history');
    }

    return await response.json();
}

/**
 * Cancel subscription (immediate or at period end)
 */
export async function cancelSubscription(
    userId: string,
    immediate: boolean,
    reason?: string
): Promise<any> {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/${userId}/cancel`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            immediate,
            reason,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to cancel subscription');
    }

    return await response.json();
}

/**
 * Reactivate canceled subscription
 */
export async function reactivateSubscription(
    userId: string,
    newPlanId?: string
): Promise<any> {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/${userId}/reactivate`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            newPlanId,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to reactivate subscription');
    }

    return await response.json();
}

/**
 * Revoke billing exemption and create paid subscription
 */
export async function revokeBillingExempt(
    userId: string,
    newPlanId: string,
    adminNotes?: string
): Promise<any> {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/${userId}/revoke-exempt`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            newPlanId,
            startDate: 'immediate',
            adminNotes,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to revoke billing exemption');
    }

    return await response.json();
}

/**
 * Format currency amount (cents to dollars)
 */
export function formatCurrency(amount: number, currency: string = 'usd'): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
    }).format(amount / 100);
}

/**
 * Format date to local string
 */
export function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Get audit logs with optional filters
 */
export async function getAuditLogs(options?: {
    limit?: number;
    userId?: string;
    action?: string;
    startDate?: string;
    endDate?: string;
}): Promise<any[]> {
    const token = await getAuthToken();

    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.userId) params.append('userId', options.userId);
    if (options?.action) params.append('action', options.action);
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);

    const response = await fetch(`${AUDIT_API_URL}/logs?${params.toString()}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch audit logs');
    }

    const data = await response.json();
    return data.logs || [];
}

/**
 * Get subscription metrics for dashboard
 */
export async function getSubscriptionMetrics(): Promise<{
    totalActiveSubscriptions: number;
    totalCancelledSubscriptions: number;
    totalBillingExempt: number;
    recentActions: number;
    trendsData: {
        activeChange: number;
        cancelledChange: number;
    };
}> {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE_URL}/metrics`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch subscription metrics');
    }

    return await response.json();
}
